import { useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Badge, Button, Card, Input, LoadingSkeleton } from '@nexora/design-system';
import { formatNumber } from '../../shared/utils/format';
import {
  TREND_LABEL,
  useModuleCtx,
  useOkrDashboard,
  useProductivityAnalysis,
  useProductivityComparative,
  type OkrStatus,
  type ProductivityTrend,
} from '../okrs/useOkrs';

/* Performance Global (protótipo erpone-dashboard-de-performance-global).
 * Números vêm de GET /okrs/dashboard, /okrs/productivity/comparative e
 * /okrs/productivity/analysis. Variação é a `variacao` do departamento;
 * não há série trimestral separada para inventar "vs último trimestre".
 */

function scoreText(value: number | null | undefined): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '—';
  return formatNumber(value, { maximumFractionDigits: 1 });
}

function gradeFromScore(value: number | null | undefined): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '—';
  if (value >= 90) return 'A';
  if (value >= 80) return 'B';
  if (value >= 70) return 'C';
  if (value >= 60) return 'D';
  return 'E';
}

function trendMark(trend: ProductivityTrend): string {
  if (trend === 'SUBINDO') return '↑';
  if (trend === 'CAINDO') return '↓';
  return '→';
}

export function PerformanceGlobalPage(): JSX.Element {
  const navigate = useNavigate();
  const ctx = useModuleCtx();
  const [periodo, setPeriodo] = useState('');
  const filter = periodo.trim() ? { periodo: periodo.trim() } : undefined;
  const dashboard = useOkrDashboard(ctx);
  const comparative = useProductivityComparative(ctx, filter);
  const analysis = useProductivityAnalysis(ctx, filter);

  const departments = useMemo(
    () => [...(comparative.data?.departamentos ?? [])].sort((a, b) => b.scoreMedio - a.scoreMedio),
    [comparative.data?.departamentos],
  );
  const okrs = dashboard.data?.okrs ?? [];
  const resumo = dashboard.data?.resumo;
  const keyResults = okrs.reduce(
    (acc, okr) => ({
      total: acc.total + okr.keyResultsCount,
      done: acc.done + okr.keyResultsConcluidos,
    }),
    { total: 0, done: 0 },
  );
  const atRisk = okrs.filter((okr) => okr.status === 'ACTIVE' && okr.progresso < 70);
  const falling = departments.filter((dept) => dept.tendencia === 'CAINDO');
  const loading = dashboard.isLoading || comparative.isLoading || analysis.isLoading;
  const periodLabel = comparative.data?.periodoAtual ?? analysis.data?.periodoAtual;

  const exportPanel = (): void => {
    const header = ['departamento', 'score', 'tendencia', 'variacao', 'operadores'];
    const lines = departments.map((dept) =>
      [dept.departamento, String(dept.scoreMedio), dept.tendencia, String(dept.variacao), String(dept.totalOperadores)]
        .map((cell) => `"${cell.replaceAll('"', '""')}"`)
        .join(','),
    );
    const blob = new Blob([[header.join(','), ...lines].join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'performance-global.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="flex flex-col gap-lg p-xl">
      <div className="flex flex-wrap items-end justify-between gap-md">
        <div>
          <h1 className="mb-xs mt-0 font-display-lg text-display-lg text-on-surface">Performance Global</h1>
          <p className="m-0 font-body-md text-body-md text-on-surface-variant">
            Produtividade, metas e departamentos{periodLabel ? ` — período ${periodLabel}` : ''}.
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-sm">
          <Input
            label="Período"
            value={periodo}
            placeholder="Ex.: 2026-Q3"
            onChange={(e) => setPeriodo(e.target.value)}
          />
          <Button variant="outline" disabled={departments.length === 0} onClick={exportPanel}>
            Exportar painel
          </Button>
        </div>
      </div>

      {!ctx ? (
        <Card>
          <p className="m-0 font-body-md text-body-md text-on-surface-variant">
            Nenhuma empresa vinculada ao tenant. O painel usa a empresa da sessão.
          </p>
        </Card>
      ) : null}

      {loading ? (
        <Card>
          <LoadingSkeleton count={3} height="72px" />
        </Card>
      ) : null}

      {[dashboard.error, comparative.error, analysis.error].filter(Boolean).map((error, index) => (
        <Card key={index}>
          <p className="m-0 font-body-sm text-body-sm text-error">{(error as Error).message}</p>
        </Card>
      ))}

      <div className="grid grid-cols-1 gap-md md:grid-cols-3">
        <Card>
          <p className="mb-xs mt-0 font-label-md text-label-md uppercase text-on-surface-variant">Produtividade global</p>
          <p className="m-0 font-display-lg text-display-lg tabular-nums text-on-surface">
            {scoreText(comparative.data?.mediaGeral)}
          </p>
          <p className="mb-0 mt-xs font-body-sm text-body-sm text-on-surface-variant">Média dos departamentos</p>
        </Card>
        <Card>
          <p className="mb-xs mt-0 font-label-md text-label-md uppercase text-on-surface-variant">Atingimento de metas</p>
          <p className="m-0 font-display-lg text-display-lg tabular-nums text-on-surface">
            {resumo ? `${scoreText(resumo.progressoMedio)}%` : '—'}
          </p>
          <p className="mb-0 mt-xs font-body-sm text-body-sm text-on-surface-variant">
            {keyResults.done}/{keyResults.total} resultados-chave concluídos · {resumo?.concluidos ?? 0}/{resumo?.total ?? 0} OKRs concluídos
          </p>
        </Card>
        <Card>
          <p className="mb-xs mt-0 font-label-md text-label-md uppercase text-on-surface-variant">Saúde dos departamentos</p>
          <p className="m-0 font-display-lg text-display-lg text-on-surface">{gradeFromScore(comparative.data?.mediaGeral)}</p>
          <p className="mb-0 mt-xs font-body-sm text-body-sm text-on-surface-variant">
            Letra da média ({scoreText(comparative.data?.mediaGeral)}) em {departments.length} departamento{departments.length === 1 ? '' : 's'}
          </p>
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-lg xl:grid-cols-3">
        <Card className="xl:col-span-2">
          <h2 className="mb-md mt-0 font-headline-sm text-headline-sm text-on-surface">Dimensões por departamento</h2>
          {departments.length === 0 ? (
            <p className="m-0 font-body-md text-body-md text-on-surface-variant">Nenhuma métrica de produtividade neste período.</p>
          ) : (
            <ul className="m-0 flex flex-col gap-sm p-0" style={{ listStyle: 'none' }}>
              {departments.map((dept) => (
                <li key={dept.departamento}>
                  <div className="mb-xs flex justify-between font-body-sm text-body-sm text-on-surface">
                    <span>{dept.departamento}</span>
                    <span>{scoreText(dept.scoreMedio)}</span>
                  </div>
                  <div className="h-2 overflow-hidden rounded-full bg-surface-container-high">
                    <div className="h-full bg-primary" style={{ width: `${Math.max(0, Math.min(100, dept.scoreMedio))}%` }} />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card padding="0">
          <div className="border-b border-outline-variant p-md">
            <h2 className="m-0 font-headline-sm text-headline-sm text-on-surface">Departamentos</h2>
          </div>
          {departments.length === 0 ? (
            <p className="m-0 p-md font-body-sm text-body-sm text-on-surface-variant">Sem ranking.</p>
          ) : (
            <ol className="m-0 p-0" style={{ listStyle: 'none' }}>
              {departments.slice(0, 5).map((dept, index) => (
                <li key={dept.departamento} className="flex items-center justify-between border-b border-outline-variant px-md py-sm last:border-b-0">
                  <span className="font-body-sm text-body-sm text-on-surface">{index + 1}. {dept.departamento}</span>
                  <span className="font-label-md text-label-md text-on-surface">
                    {scoreText(dept.scoreMedio)} {trendMark(dept.tendencia)} {scoreText(Math.abs(dept.variacao))}
                  </span>
                </li>
              ))}
            </ol>
          )}
          <div className="border-t border-outline-variant p-md">
            <Button variant="ghost" onClick={() => void navigate({ to: '/okrs/productivity/comparative' })}>
              Ver ranking completo
            </Button>
          </div>
        </Card>
      </div>

      <Card padding="0">
        <div className="flex items-center justify-between border-b border-outline-variant p-md">
          <h2 className="m-0 font-headline-sm text-headline-sm text-on-surface">Indicadores em risco</h2>
          <Badge variant="error">{atRisk.length + falling.length + (analysis.data?.gargalo ? 1 : 0)}</Badge>
        </div>
        {atRisk.length === 0 && falling.length === 0 && !analysis.data?.gargalo ? (
          <p className="m-0 p-md font-body-md text-body-md text-on-surface-variant">Nenhum OKR ativo abaixo de 70% e nenhum departamento em queda.</p>
        ) : (
          <ul className="m-0 flex flex-col p-0" style={{ listStyle: 'none' }}>
            {analysis.data?.gargalo ? (
              <li className="border-b border-outline-variant p-md">
                <p className="mb-xs mt-0 font-body-md text-body-md font-semibold text-on-surface">
                  Gargalo em {analysis.data.gargalo.departamento}
                </p>
                <p className="m-0 font-body-sm text-body-sm text-on-surface-variant">
                  Score médio {scoreText(analysis.data.gargalo.scoreMedio)}. Pior operador: {analysis.data.gargalo.piorOperador}.
                </p>
              </li>
            ) : null}
            {falling.map((dept) => (
              <li key={dept.departamento} className="border-b border-outline-variant p-md last:border-b-0">
                <p className="mb-xs mt-0 font-body-md text-body-md font-semibold text-on-surface">{dept.departamento}</p>
                <p className="m-0 font-body-sm text-body-sm text-on-surface-variant">
                  {TREND_LABEL[dept.tendencia]} · variação {scoreText(dept.variacao)} · score {scoreText(dept.scoreMedio)}
                </p>
              </li>
            ))}
            {atRisk.map((okr) => (
              <li key={okr.id} className="flex flex-wrap items-center justify-between gap-sm border-b border-outline-variant p-md last:border-b-0">
                <div>
                  <p className="mb-xs mt-0 font-body-md text-body-md font-semibold text-on-surface">{okr.titulo}</p>
                  <p className="m-0 font-body-sm text-body-sm text-on-surface-variant">
                    {okr.periodo} · progresso {scoreText(okr.progresso)}% · {statusLabel(okr.status)}
                  </p>
                </div>
                <Button size="sm" variant="outline" onClick={() => void navigate({ to: '/okrs' })}>
                  Abrir OKRs
                </Button>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

function statusLabel(status: OkrStatus): string {
  if (status === 'ACTIVE') return 'Ativo';
  if (status === 'COMPLETED') return 'Concluído';
  if (status === 'ARCHIVED') return 'Arquivado';
  return 'Rascunho';
}
